import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Save, DollarSign, Zap, ShieldCheck, Package,
  Loader2, Check, X, AlertTriangle, CheckCircle2, Plus, Copy, Eye, Building2,
} from "lucide-react";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

const CATEGORY_ICON = {
  empresarial: Package, pessoa: Package, dossie: Package, judicial: Package,
  compliance: ShieldCheck, fiscal: DollarSign, watch: Zap,
  contratacoes: Package, minerario: Package, ambiental: Package, rural: Package,
  legislativo: Package, calculos: DollarSign, mercado: DollarSign, obras: Package,
  adicional: Package,
};

const SYNC_BADGE = {
  synced:   { c: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "SYNCED" },
  syncing:  { c: "bg-blue-50 text-blue-700 border-blue-300", label: "SYNCING" },
  pending:  { c: "bg-amber-50 text-amber-700 border-amber-300", label: "PENDING" },
  failed:   { c: "bg-rose-50 text-rose-700 border-rose-300", label: "FAILED" },
};

export default function AdminIntelligence() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quotas, setQuotas] = useState({ plans: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit'|'duplicate', product?}

  const load = async () => {
    try {
      setLoading(true);
      const [p, c, q] = await Promise.all([
        api.get("/intel/admin/products"),
        api.get("/intel/admin/categories").catch(() => ({ data: { items: [] } })),
        api.get("/intel/admin/plan-quotas"),
      ]);
      setProducts(p.data.items || []);
      setCategories(c.data.items || []);
      setQuotas(q.data || { plans: [], items: [] });
    } catch (e) {
      toast.error("Falha ao carregar dados admin");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seedCategoriesIfEmpty = async () => {
    if (categories.length) return;
    try {
      await api.post("/intel/admin/categories/seed");
      const c = await api.get("/intel/admin/categories");
      setCategories(c.data.items || []);
      toast.success("Categorias inicializadas");
    } catch (e) { toast.error("Falha ao seed categorias"); }
  };

  const patchProduct = async (slug, patch) => {
    setBusy(slug);
    try {
      const r = await api.patch(`/intel/admin/products/${slug}`, patch);
      setProducts(prev => prev.map(p => p.slug === slug ? r.data : p));
      toast.success(`Produto ${slug} atualizado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar");
    } finally { setBusy(null); }
  };

  const syncOne = async (slug) => {
    setBusy(slug);
    try {
      const r = await api.post(`/intel/admin/products/${slug}/sync-stripe`);
      setProducts(prev => prev.map(p => p.slug === slug ? r.data : p));
      toast.success(`${slug} sincronizado com Stripe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync Stripe falhou");
    } finally { setBusy(null); }
  };

  const bulkSync = async () => {
    setBusy("__bulk__");
    try {
      const r = await api.post("/intel/admin/products/bulk-sync-stripe");
      toast.success(`${r.data.total} produtos sincronizados`);
      await load();
    } catch (e) {
      toast.error("Bulk sync falhou");
    } finally { setBusy(null); }
  };

  const activateLaunch = async () => {
    setBusy("__launch__");
    try {
      const r = await api.post("/intel/admin/products/activate-launch");
      toast.success(`${r.data.results.length} produtos de lançamento ativos`);
      await load();
    } catch (e) {
      toast.error("Ativação falhou");
    } finally { setBusy(null); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="admin-intelligence-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Link to="/inteligencia" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3"/> Voltar
            </Link>
            <h1 className="mt-1 font-heading text-3xl font-semibold" style={{ color: NAVY }}>
              Admin · Inteligência 360
            </h1>
            <p className="text-sm text-slate-500">
              CRUD dos 37 produtos comerciais + sync Stripe + matriz de quotas por plano
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => { seedCategoriesIfEmpty(); setModal({ mode: "create" }); }}
              data-testid="btn-new-product"
              className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium inline-flex items-center gap-1">
              <Plus className="w-4 h-4"/> Novo produto
            </button>
            <button onClick={activateLaunch}
              disabled={busy === "__launch__"}
              data-testid="btn-activate-launch"
              className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-1">
              <Zap className="w-4 h-4"/> Ativar Fase 2
            </button>
            <button onClick={bulkSync}
              disabled={busy === "__bulk__"}
              data-testid="btn-bulk-sync"
              className="px-3 py-2 rounded-lg text-white text-sm font-medium inline-flex items-center gap-1"
              style={{ background: NAVY }}>
              {busy === "__bulk__" ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
              Sync Stripe (ativos)
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200 mb-4">
          <div className="flex gap-4">
            {[
              {id:"products", label:`Produtos (${products.length})`},
              {id:"categories", label:`Categorias (${categories.length})`},
              {id:"quotas", label:"Quotas por plano"},
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-1 py-2 text-sm font-medium border-b-2 transition
                    ${tab === t.id ? "border-slate-800 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2"/> Carregando…
          </div>
        ) : tab === "products" ? (
          <ProductsTable products={products} busy={busy}
            onPatch={patchProduct} onSync={syncOne}
            onDuplicate={(p) => setModal({ mode: "duplicate", product: p })}/>
        ) : tab === "categories" ? (
          <CategoriesTable
            categories={categories}
            products={products}
            onReload={load}
            onSeed={seedCategoriesIfEmpty}/>
        ) : (
          <QuotasMatrix products={products} quotas={quotas} onReload={load}/>
        )}
      </div>
      {modal && (
        <ProductModal
          mode={modal.mode}
          product={modal.product}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}/>
      )}
    </div>
  );
}

function ProductsTable({ products, busy, onPatch, onSync, onDuplicate }) {
  const [priceEdits, setPriceEdits] = useState({});
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="products-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Produto</th>
              <th className="text-left px-3 py-2">Categoria</th>
              <th className="text-right px-3 py-2">Preço (R$)</th>
              <th className="text-center px-3 py-2">Ativo</th>
              <th className="text-center px-3 py-2">Destaque</th>
              <th className="text-center px-3 py-2">Stripe</th>
              <th className="text-right px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
          {products.map(p => {
            const Icon = CATEGORY_ICON[p.category] || Package;
            const editedPrice = priceEdits[p.slug];
            const currentPriceReais = (p.current_price_cents / 100).toFixed(2);
            const priceDirty = editedPrice !== undefined && editedPrice !== currentPriceReais;
            const sync = SYNC_BADGE[p.stripe_sync_status] || SYNC_BADGE.pending;
            return (
              <tr key={p.slug} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`row-${p.slug}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 shrink-0" style={{ color: GOLD }}/>
                    <div>
                      <div className="font-medium" style={{ color: NAVY }}>{p.name}</div>
                      <div className="text-[10px] font-mono text-slate-400">{p.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500">{p.category}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <input type="number" step="0.10" min="0"
                      value={editedPrice ?? currentPriceReais}
                      onChange={(e) => setPriceEdits(s => ({...s, [p.slug]: e.target.value}))}
                      data-testid={`price-input-${p.slug}`}
                      className="w-24 border rounded px-2 py-1 text-right text-sm"/>
                    {priceDirty && (
                      <button
                        title="Salvar preço"
                        data-testid={`save-price-${p.slug}`}
                        onClick={() => {
                          const cents = Math.round(parseFloat(editedPrice) * 100);
                          if (Number.isFinite(cents) && cents > 0) {
                            onPatch(p.slug, { current_price_cents: cents });
                            setPriceEdits(s => { const c={...s}; delete c[p.slug]; return c; });
                          }
                        }}
                        disabled={busy === p.slug}
                        className="p-1 rounded bg-emerald-600 text-white">
                        <Save className="w-3 h-3"/>
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-center">
                  <Toggle checked={p.active}
                    data-testid={`toggle-active-${p.slug}`}
                    onChange={(v) => onPatch(p.slug, { active: v })}
                    disabled={busy === p.slug}/>
                </td>
                <td className="px-3 py-2 text-center">
                  <Toggle checked={p.featured}
                    data-testid={`toggle-featured-${p.slug}`}
                    onChange={(v) => onPatch(p.slug, { featured: v })}
                    disabled={busy === p.slug}/>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] tracking-widest border rounded px-1.5 py-0.5 ${sync.c}`}
                        title={p.stripe_sync_error || ""}
                        data-testid={`sync-status-${p.slug}`}>
                    {sync.label}
                  </span>
                  {p.stripe_price_id && (
                    <div className="text-[9px] font-mono text-slate-400 mt-0.5">{p.stripe_price_id.slice(-8)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onSync(p.slug)}
                    disabled={busy === p.slug || !p.active}
                    data-testid={`sync-${p.slug}`}
                    className="text-xs px-2 py-1 border rounded hover:bg-slate-100 disabled:opacity-40 inline-flex items-center gap-1 mr-1">
                    {busy === p.slug ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                    Sync
                  </button>
                  <button
                    onClick={() => onDuplicate(p)}
                    data-testid={`dup-${p.slug}`}
                    className="text-xs px-2 py-1 border rounded hover:bg-slate-100 inline-flex items-center gap-1">
                    <Copy className="w-3 h-3"/> Dup
                  </button>
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoriesTable({ categories, products, onReload, onSeed }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState({}); // { [slug]: draft }
  const [busy, setBusy] = useState(null);

  const usageCount = useMemo(() => {
    const m = {};
    for (const p of products) {
      const k = p.category || "outros";
      m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [products]);

  const startEdit = (c) => setEditing(s => ({ ...s, [c.slug]: {
    name: c.name, button_color: c.button_color || "#334155",
    sort_order: c.sort_order || 100, icon: c.icon || "Sparkles",
    active: !!c.active,
  }}));
  const cancelEdit = (slug) => setEditing(s => { const x = {...s}; delete x[slug]; return x; });

  const save = async (slug) => {
    const d = editing[slug]; if (!d) return;
    setBusy(slug);
    try {
      await api.patch(`/intel/admin/categories/${slug}`, d);
      toast.success(`Categoria ${slug} atualizada`);
      cancelEdit(slug); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
    finally { setBusy(null); }
  };
  const remove = async (slug) => {
    if (!window.confirm(`Excluir categoria "${slug}"?`)) return;
    setBusy(slug);
    try {
      await api.delete(`/intel/admin/categories/${slug}`);
      toast.success("Categoria excluída"); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao excluir"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="categories-table">
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">
          Cada categoria define a <b>cor do botão CTA</b> e o <b>ícone</b> do agrupamento na Inteligência 360.
          Categoria em uso não pode ser excluída — mova os produtos primeiro.
        </p>
        <div className="flex gap-2">
          <button onClick={onSeed} data-testid="btn-seed-cats"
            className="text-xs px-3 py-1.5 border rounded hover:bg-slate-100">
            Seed defaults
          </button>
          <button onClick={() => setCreating(true)} data-testid="btn-new-category"
            className="text-xs px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5"/> Nova categoria
          </button>
        </div>
      </div>

      {creating && (
        <NewCategoryRow onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); onReload(); }}/>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Slug</th>
              <th className="text-left px-3 py-2">Nome</th>
              <th className="text-left px-3 py-2">Ícone</th>
              <th className="text-center px-3 py-2">Cor</th>
              <th className="text-right px-3 py-2">Ordem</th>
              <th className="text-center px-3 py-2">Ativa</th>
              <th className="text-right px-3 py-2">Produtos</th>
              <th className="text-right px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
          {categories.length === 0 && (
            <tr><td colSpan={8} className="text-center py-6 text-slate-400 text-sm">
              Nenhuma categoria — clique em <b>Seed defaults</b> para popular.
            </td></tr>
          )}
          {categories.map(c => {
            const isEditing = editing[c.slug];
            const d = isEditing || {};
            const use = usageCount[c.slug] || 0;
            return (
              <tr key={c.slug} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`cat-row-${c.slug}`}>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{c.slug}</td>
                <td className="px-3 py-2">
                  {isEditing
                    ? <input value={d.name} onChange={e=>setEditing(s=>({...s, [c.slug]: {...d, name: e.target.value}}))}
                        className="w-full border rounded px-2 py-1 text-sm" data-testid={`cat-name-${c.slug}`}/>
                    : <span className="font-medium" style={{ color: NAVY }}>{c.name}</span>}
                </td>
                <td className="px-3 py-2 text-xs font-mono">
                  {isEditing
                    ? <input value={d.icon} onChange={e=>setEditing(s=>({...s, [c.slug]: {...d, icon: e.target.value}}))}
                        placeholder="Sparkles"
                        className="w-32 border rounded px-2 py-1 text-xs" data-testid={`cat-icon-${c.slug}`}/>
                    : <span className="text-slate-500">{c.icon || "Sparkles"}</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {isEditing ? (
                    <div className="inline-flex items-center gap-1">
                      <input type="color" value={d.button_color}
                        onChange={e=>setEditing(s=>({...s, [c.slug]: {...d, button_color: e.target.value}}))}
                        className="w-8 h-6 border rounded" data-testid={`cat-color-${c.slug}`}/>
                      <input value={d.button_color}
                        onChange={e=>setEditing(s=>({...s, [c.slug]: {...d, button_color: e.target.value}}))}
                        className="w-20 border rounded px-1 py-0.5 text-[10px] font-mono"/>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded border border-slate-300 inline-block"
                            style={{ background: c.button_color || "#334155" }}/>
                      <code className="text-[10px] text-slate-500">{c.button_color || "#334155"}</code>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {isEditing
                    ? <input type="number" value={d.sort_order}
                        onChange={e=>setEditing(s=>({...s, [c.slug]: {...d, sort_order: parseInt(e.target.value)||100}}))}
                        className="w-16 border rounded px-2 py-1 text-right text-sm"/>
                    : <span className="text-slate-600">{c.sort_order}</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <Toggle checked={isEditing ? d.active : c.active}
                    disabled={!isEditing || busy === c.slug}
                    onChange={(v)=>setEditing(s=>({...s, [c.slug]: {...d, active: v}}))}
                    data-testid={`cat-active-${c.slug}`}/>
                </td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">{use}</td>
                <td className="px-3 py-2 text-right">
                  {isEditing ? (
                    <div className="inline-flex gap-1">
                      <button onClick={() => save(c.slug)} disabled={busy === c.slug}
                        data-testid={`cat-save-${c.slug}`}
                        className="text-xs px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1">
                        {busy === c.slug ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>} Salvar
                      </button>
                      <button onClick={() => cancelEdit(c.slug)}
                        className="text-xs px-2 py-1 border rounded hover:bg-slate-100">Cancelar</button>
                    </div>
                  ) : (
                    <div className="inline-flex gap-1">
                      <button onClick={() => startEdit(c)}
                        data-testid={`cat-edit-${c.slug}`}
                        className="text-xs px-2 py-1 border rounded hover:bg-slate-100">Editar</button>
                      <button onClick={() => remove(c.slug)}
                        disabled={use > 0 || busy === c.slug}
                        title={use > 0 ? `Em uso por ${use} produto(s)` : "Excluir"}
                        data-testid={`cat-del-${c.slug}`}
                        className="text-xs px-2 py-1 border rounded hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed">
                        Excluir
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewCategoryRow({ onCancel, onSaved }) {
  const [d, setD] = useState({
    slug: "", name: "", button_color: "#0B1B2F",
    icon: "Sparkles", sort_order: 200, active: true, show_as_filter: true,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!d.slug || !d.name) { toast.error("slug e nome obrigatórios"); return; }
    setSaving(true);
    try {
      await api.post("/intel/admin/categories", d);
      toast.success("Categoria criada"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao criar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border-2 border-emerald-300 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="new-category-row">
      <input placeholder="slug (kebab)" value={d.slug}
        onChange={e=>setD(s=>({...s, slug: e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g,"")}))}
        className="w-40 border rounded px-2 py-1 text-sm font-mono" data-testid="new-cat-slug"/>
      <input placeholder="Nome exibido" value={d.name}
        onChange={e=>setD(s=>({...s, name: e.target.value}))}
        className="flex-1 min-w-[160px] border rounded px-2 py-1 text-sm" data-testid="new-cat-name"/>
      <input placeholder="Icon" value={d.icon}
        onChange={e=>setD(s=>({...s, icon: e.target.value}))}
        className="w-32 border rounded px-2 py-1 text-xs font-mono" data-testid="new-cat-icon"/>
      <div className="inline-flex items-center gap-1">
        <input type="color" value={d.button_color}
          onChange={e=>setD(s=>({...s, button_color: e.target.value}))}
          className="w-8 h-7 border rounded" data-testid="new-cat-color"/>
        <input value={d.button_color}
          onChange={e=>setD(s=>({...s, button_color: e.target.value}))}
          className="w-20 border rounded px-1 py-0.5 text-[10px] font-mono"/>
      </div>
      <input type="number" placeholder="Ordem" value={d.sort_order}
        onChange={e=>setD(s=>({...s, sort_order: parseInt(e.target.value)||200}))}
        className="w-20 border rounded px-2 py-1 text-sm"/>
      <button onClick={submit} disabled={saving}
        data-testid="new-cat-save"
        className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1">
        {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>} Criar
      </button>
      <button onClick={onCancel} className="text-xs px-3 py-1.5 border rounded hover:bg-slate-100">Cancelar</button>
    </div>
  );
}


function Toggle({ checked, onChange, disabled, ...rest }) {
  return (
    <button
      role="switch" aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      {...rest}
      className={`inline-flex items-center w-9 h-5 rounded-full transition
        ${checked ? "bg-emerald-500" : "bg-slate-300"} ${disabled ? "opacity-60" : ""}`}>
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition
        ${checked ? "translate-x-4" : "translate-x-0.5"}`}/>
    </button>
  );
}

function QuotasMatrix({ products, quotas, onReload }) {
  const plans = quotas.plans || [];
  const items = quotas.items || [];
  const map = useMemo(() => {
    const m = {};
    items.forEach(q => { m[`${q.plan}::${q.product_slug}`] = q.monthly_quota; });
    return m;
  }, [items]);
  const [edits, setEdits] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const save = async (plan, slug, value) => {
    const qty = parseInt(value, 10);
    if (!Number.isFinite(qty) || qty < 0) { toast.error("Quota inválida"); return; }
    const key = `${plan}::${slug}`;
    setBusyKey(key);
    try {
      await api.put("/intel/admin/plan-quotas",
        { plan, product_slug: slug, monthly_quota: qty });
      toast.success(`${slug} · ${plan} = ${qty}`);
      setEdits(s => { const c = {...s}; delete c[key]; return c; });
      await onReload();
    } catch (e) {
      toast.error("Falha ao salvar");
    } finally { setBusyKey(null); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="quotas-matrix">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Produto</th>
              {plans.map(pl => (
                <th key={pl} className="text-center px-3 py-2">{pl}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.slug} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium text-sm" style={{ color: NAVY }}>{p.name}</div>
                  <div className="text-[10px] font-mono text-slate-400">{p.slug}</div>
                </td>
                {plans.map(pl => {
                  const key = `${pl}::${p.slug}`;
                  const val = edits[key] ?? (map[key] ?? "");
                  const dirty = edits[key] !== undefined && String(edits[key]) !== String(map[key] ?? "");
                  return (
                    <td key={pl} className="px-3 py-2 text-center">
                      <div className="inline-flex items-center gap-1">
                        <input type="number" min="0"
                          placeholder="—"
                          value={val}
                          onChange={(e) => setEdits(s => ({...s, [key]: e.target.value}))}
                          data-testid={`quota-input-${pl}-${p.slug}`}
                          className="w-16 border rounded px-1 py-1 text-center text-sm"/>
                        {dirty && (
                          <button
                            data-testid={`quota-save-${pl}-${p.slug}`}
                            onClick={() => save(pl, p.slug, edits[key])}
                            disabled={busyKey === key}
                            className="p-1 rounded bg-emerald-600 text-white">
                            {busyKey === key ? <Loader2 className="w-3 h-3 animate-spin"/> : <Check className="w-3 h-3"/>}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 px-4 py-3 bg-slate-50 border-t">
        Quotas representam consultas mensais permitidas por plano. Vazio = sem quota configurada.
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// ProductModal · criar / editar / duplicar com preview em tempo real
// -------------------------------------------------------------------------
function ProductModal({ mode, product, categories, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const isDup = mode === "duplicate";
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => {
    if (isDup && product) {
      return {
        ...emptyForm(),
        ...product,
        slug: `${product.slug}-copia`,
        name: `${product.name} (cópia)`,
        active: false,
        current_price_cents: product.current_price_cents || 0,
      };
    }
    if (isEdit && product) return { ...emptyForm(), ...product };
    return emptyForm(categories[0]?.slug);
  });

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const cat = categories.find(c => c.slug === form.category) || {};
  const buttonColor = cat.button_color || "#0B1B2F";
  const priceReais = (Number(form.current_price_cents || 0) / 100).toFixed(2).replace(".", ",");

  const validate = () => {
    if (!form.slug || !/^[a-z0-9][a-z0-9\-]{1,60}$/.test(form.slug)) return "Slug inválido (kebab-case)";
    if (!form.name?.trim()) return "Nome obrigatório";
    if (form.current_price_cents < 0) return "Preço inválido";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/intel/admin/products/${form.slug}`, {
          name: form.name, category: form.category,
          short_description: form.short_description, full_description: form.full_description,
          icon: form.icon, status: form.status,
          current_price_cents: Number(form.current_price_cents),
          unit_label: form.unit_label, billing_mode: form.billing_mode,
          show_in_intelligence_360: form.show_in_intelligence_360,
          channels: form.channels, sort_order: Number(form.sort_order) || 100,
          active: form.active, featured: form.featured,
          action_type: form.action_type, action_target: form.action_target,
          badge: form.badge || null,
        });
        toast.success("Produto atualizado");
      } else if (isDup) {
        await api.post(`/intel/admin/products/${product.slug}/duplicate`, {
          new_slug: form.slug, new_name: form.name,
        });
        toast.success("Produto duplicado (nasce inativo)");
      } else {
        await api.post(`/intel/admin/products`, form);
        toast.success("Produto criado");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-5" onClick={e=>e.stopPropagation()} data-testid="product-modal">
        <div className="md:col-span-3 p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold" style={{ color: NAVY }}>
              {isEdit ? "Editar produto" : isDup ? "Duplicar produto" : "Novo produto"}
            </h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5"/></button>
          </div>
          <div className="space-y-3 text-sm">
            <Row label="Nome*"><input value={form.name} onChange={e=>set("name",e.target.value)} data-testid="pm-name" className="w-full border rounded px-3 py-2"/></Row>
            <Row label="Slug* (kebab-case)"><input value={form.slug} onChange={e=>set("slug",e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g,""))} disabled={isEdit} data-testid="pm-slug" className="w-full border rounded px-3 py-2 font-mono text-xs disabled:bg-slate-100"/></Row>
            <Row label="Categoria*"><select value={form.category} onChange={e=>set("category",e.target.value)} data-testid="pm-category" className="w-full border rounded px-3 py-2">
              {categories.length === 0 && <option value="">(rode Seed Categorias primeiro)</option>}
              {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select></Row>
            <Row label="Descrição curta"><textarea value={form.short_description} onChange={e=>set("short_description",e.target.value)} rows={2} maxLength={200} data-testid="pm-short-desc" className="w-full border rounded px-3 py-2"/></Row>
            <Row label="Descrição completa"><textarea value={form.full_description} onChange={e=>set("full_description",e.target.value)} rows={3} data-testid="pm-full-desc" className="w-full border rounded px-3 py-2"/></Row>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Ícone (Lucide)"><input value={form.icon} onChange={e=>set("icon",e.target.value)} data-testid="pm-icon" placeholder="Sparkles" className="w-full border rounded px-3 py-2 font-mono text-xs"/></Row>
              <Row label="Status"><select value={form.status} onChange={e=>set("status",e.target.value)} data-testid="pm-status" className="w-full border rounded px-3 py-2">
                <option value="ACTIVE">ACTIVE</option><option value="COMING_SOON">COMING_SOON</option><option value="MAINTENANCE">MAINTENANCE</option><option value="HIDDEN">HIDDEN</option>
              </select></Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Preço (centavos)"><input type="number" value={form.current_price_cents} onChange={e=>set("current_price_cents",parseInt(e.target.value)||0)} data-testid="pm-price" className="w-full border rounded px-3 py-2"/></Row>
              <Row label="Cobrança"><select value={form.billing_mode} onChange={e=>set("billing_mode",e.target.value)} data-testid="pm-billing" className="w-full border rounded px-3 py-2">
                <option value="PER_QUERY">Por consulta</option><option value="MONTHLY">Mensal</option><option value="YEARLY">Anual</option><option value="ONE_TIME">Única</option><option value="FREE">Grátis</option><option value="QUOTE">Sob consulta</option>
              </select></Row>
            </div>
            <Row label="Rótulo da cobrança (ex: 'Por consulta')"><input value={form.unit_label} onChange={e=>set("unit_label",e.target.value)} data-testid="pm-unit-label" className="w-full border rounded px-3 py-2"/></Row>
            <div className="grid grid-cols-3 gap-3">
              <Row label="Ordem"><input type="number" value={form.sort_order} onChange={e=>set("sort_order",parseInt(e.target.value)||100)} data-testid="pm-order" className="w-full border rounded px-3 py-2"/></Row>
              <Row label="Ativo"><Toggle checked={!!form.active} onChange={v=>set("active",v)} data-testid="pm-active"/></Row>
              <Row label="Destaque"><Toggle checked={!!form.featured} onChange={v=>set("featured",v)} data-testid="pm-featured"/></Row>
            </div>
            <Row label="Exibir na Inteligência 360">
              <Toggle checked={!!form.show_in_intelligence_360} data-testid="pm-show-i360"
                onChange={(v) => setForm(s => ({
                  ...s,
                  show_in_intelligence_360: v,
                  channels: v ? Array.from(new Set([...(s.channels||[]), "intelligence360"])) : (s.channels||[]).filter(c => c !== "intelligence360")
                }))}/>
            </Row>
            <Row label="Badge opcional (Novo · Popular · Recomendado · Em breve)"><input value={form.badge||""} onChange={e=>set("badge",e.target.value||null)} data-testid="pm-badge" className="w-full border rounded px-3 py-2"/></Row>
          </div>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={onClose} className="px-4 py-2 rounded border text-sm">Cancelar</button>
            <button onClick={submit} disabled={saving} data-testid="pm-save"
              className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
              {isEdit ? "Salvar alterações" : isDup ? "Duplicar" : "Criar produto"}
            </button>
          </div>
        </div>
        {/* PREVIEW */}
        <div className="md:col-span-2 bg-slate-100 p-6 flex flex-col" data-testid="pm-preview">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 inline-flex items-center gap-1">
            <Eye className="w-3.5 h-3.5"/> Pré-visualização do card
          </div>
          <div className="flex-1 flex items-start justify-center pt-4">
            <div className="w-full max-w-xs h-[354px] flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
                  <Building2 className="w-5 h-5" style={{ color: GOLD }}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: buttonColor }}>
                    {cat.name || form.category || "—"}
                  </div>
                  <div className="font-semibold text-base leading-tight" style={{ color: NAVY }}>{form.name || "Nome do produto"}</div>
                  <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{form.short_description || "Descrição curta que aparece no card."}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{form.unit_label || "Por consulta"}</div>
                  <div className="text-2xl font-heading font-semibold" style={{ color: NAVY }}>R$ {priceReais}</div>
                </div>
              </div>
              <ul className="mt-4 text-sm text-slate-700 space-y-1.5 flex-1">
                {(form.full_description || "").split("\n").filter(Boolean).slice(0,3).map((l,i)=><li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{color:GOLD}}/><span className="line-clamp-2">{l}</span></li>)}
                {(form.full_description||"").split("\n").filter(Boolean).length === 0 && (
                  <li className="text-slate-400 italic text-xs">Linhas da descrição completa viram bullets aqui.</li>
                )}
              </ul>
              <div className="mt-5">
                <button
                  disabled={form.status !== "ACTIVE"}
                  className="w-full py-2.5 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: form.status === "ACTIVE" ? buttonColor : "#94a3b8" }}>
                  {form.status !== "ACTIVE" ? "Indisponível" : `Consultar por R$ ${priceReais}`}
                </button>
              </div>
              {form.badge && (
                <div className="absolute mt-2 text-[10px] uppercase tracking-widest text-slate-500">
                  Badge: {form.badge}
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 text-[10px] text-slate-500 text-center">
            Cor derivada da categoria <b>{cat.slug || "—"}</b> · <span className="font-mono">{buttonColor}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

function emptyForm(defaultCategory = "empresarial") {
  return {
    slug: "", name: "", category: defaultCategory,
    short_description: "", full_description: "",
    icon: "Sparkles", status: "ACTIVE",
    current_price_cents: 990, billing_mode: "PER_QUERY", unit_label: "Por consulta",
    active: true, featured: false, sort_order: 100,
    show_in_intelligence_360: true, channels: ["intelligence360"],
    action_type: "OPEN_QUERY", action_target: "",
    badge: null,
  };
}
